import {
  ContentRegistrationType,
  RecordOrigin,
  UserInfo,
  XMLGenerationStrategy,
} from '@/common/types';
import { FormData } from '@content-registration/types';
import type { Dissertation } from '@generated/form-types/dissertation';
import {
  createCrossrefDocument,
  appendElement,
  appendElementIf,
  serializeXml,
} from '../xml-helpers';
import {
  buildHead,
  buildContributors,
  buildTitles,
  buildAbstracts,
  buildDate,
  buildIsbns,
  buildDoiData,
  buildCitationList,
  buildInstitution,
} from '../builders';

export class DissertationXmlRenderingStrategy implements XMLGenerationStrategy {
  async generateXML(
    _recordType: ContentRegistrationType,
    data: FormData,
    prettyPrint = false,
    userInfo?: UserInfo,
    origin?: RecordOrigin
  ): Promise<string> {
    const dissertationData = data as unknown as Dissertation;
    const meta = dissertationData.dissertationMetadata;

    const doc = createCrossrefDocument();
    const root = doc.documentElement;

    // <head>
    buildHead(root, userInfo);

    // <body>
    const body = appendElement(root, 'body');
    const dissertation = appendElement(body, 'dissertation');

    // 1. contributors
    buildContributors(dissertation, meta.contributors);

    // 2. titles
    buildTitles(dissertation, meta.title);

    // 3. jats:abstract (optional)
    buildAbstracts(dissertation, meta.abstracts);

    // 4. approval_date
    buildDate(dissertation, 'approval_date', meta.approvalDate);

    // 5. institution
    buildInstitution(
      dissertation,
      meta.approvingInstitution.institutionName,
      meta.approvingInstitution.rorId
    );

    // 6. degree (optional)
    appendElementIf(dissertation, 'degree', meta.degree);

    // 7. isbn (optional)
    buildIsbns(dissertation, meta.isbn);

    // 8. publisher_item with DAI identifier (optional)
    if (meta.daiIdentifier) {
      const pubItem = appendElement(dissertation, 'publisher_item');
      appendElement(pubItem, 'identifier', meta.daiIdentifier, {
        id_type: 'dai',
      });
    }

    // 9. doi_data
    buildDoiData(dissertation, meta.doi, meta.landingPageUrl);

    // 10. citation_list (optional)
    buildCitationList(
      dissertation,
      meta.references,
      origin?.resourceFieldsImported?.references === true &&
        !meta.references?.trim()
    );

    return serializeXml(doc, prettyPrint);
  }
}
